import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TaxTypeEnum } from '../../../../data/logic/tax.service';
import { AccountingSyncSettings, RepoAccountingSyncService } from '../../../../data/repositories/repo-accountingSync.service';
import { RepoCommonService } from '../../../../data/repositories/repo-common.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';

interface FlattenedTaxProfile {
    code: string;
    description: string;
    profileDescription: string;
    profileShortCode: string;
    rate: number;
    type: TaxTypeEnum;
    defaults: Array<number>;
    exempt: boolean;
}

enum TaxType {
    Income = 0,
    Expense = 1,
}

interface TaxCode {
    taxProfiles: FlattenedTaxProfile[];
    taxCode: string;
    taxDescription?: string;
}

interface TrackingCategory {
    value: string;
    label: string;
}

@Component({
    selector: 'generic-provider-settings',
    styleUrls: ['./generic-provider-settings.component.scss'],
    templateUrl: './generic-provider-settings.component.html',
    standalone: false,
})
export class GenericProviderComponent implements OnInit {
    SharedData: SharedDataService;
    AccountingSync: RepoAccountingSyncService;
    Common: RepoCommonService;

    focused = {
        startDate: false,
        purchaseOrderAccountCode: false,
        purchaseOrderAccountCodeContractor: false,
        taxRateExpenseTaxable: false,
        taxRateExpenseFree: false,
        taxExpenseCode: false,
        invoiceAccountCode: false,
        defaultInvoiceStatus: false,
        taxRateIncomeTaxable: false,
        taxRateIncomeFree: false,
        incomeTaxCode: false,
        trackingCategory: false,
    };
    constructor(
        private bsModalRef: BsModalRef,
        _sharedData: SharedDataService,
        _common: RepoCommonService,
        _accountingSync: RepoAccountingSyncService,
        private translateService: TranslateService
    ) {
        this.SharedData = _sharedData;
        this.AccountingSync = _accountingSync;
        this.Common = _common;
    }

    @Input() provider: any;
    @Input() setting?: AccountingSyncSettings;
    @Output() closemodal = new EventEmitter<boolean>();
    expenseAccounts: any;
    // @ts-ignore TS2564
    expenseTaxCodes: TaxCode[];
    // @ts-ignore TS2564
    incomeTaxCodes: TaxCode[];
    // @ts-ignore TS2564
    trackingCategories: TrackingCategory[];
    incomeAccounts: any;
    // @ts-ignore TS2564
    defaultInvoiceStatuses: any[];
    // @ts-ignore TS2564
    TaxProfiles: FlattenedTaxProfile[];
    taxRates: any;
    locale: any;
    results: any;
    syncStartDate?: Date;
    startDate?: Date;

    ngOnInit(): void {
        this.InitTaxInfo();
        this.GetAccounts();
        this.GetTaxRates();
        this.GetTrackingCategories();
        this.results = JSON.parse(this.setting?.taxSettings || 'null') || {
            expense: {},
            income: {},
        };

        this.syncStartDate = this.startDate = new Date(this.setting?.syncStartDate as string);

        this.translateService.get('general.dateFormat').subscribe((res: any) => {
            this.locale = res;
        });
        this.defaultInvoiceStatuses = ['DRAFT', 'AUTHORISED', 'SUBMITTED'];
    }

    InitTaxInfo(): void {
        if (!this.SharedData.Common.TaxProfiles || this.SharedData.Common.TaxProfiles.length === 0) {
            // @ts-ignore TS2322
            this.TaxProfiles = null;
            this.Common.GetTaxProfiles().then(() => {
                this.parseTaxRates();
            });
        } else {
            this.parseTaxRates();
        }
    }

    GetAccounts(): void {
        this.AccountingSync.GetAccounts(this.provider).then((result?: { expense: any; income: any }) => {
            if (result) {
                this.expenseAccounts = result.expense;
                this.incomeAccounts = result.income;
            }
        });
    }

    GetTaxRates(): void {
        this.AccountingSync.GetTaxRates(this.provider).then((result: { data: { taxRates: any } }) => {
            this.taxRates = result.data.taxRates;
        });
    }

    GetTrackingCategories(): void {
        this.trackingCategories = [
            {
                // @ts-ignore TS2322
                value: null,
                label: '(None)',
            },
        ];

        if (this.provider.toLowerCase() !== 'xero') return;

        this.AccountingSync.GetXeroTrackingCategories().then((result: { data?: string[] }) => {
            if (result?.data) {
                this.trackingCategories = this.trackingCategories.concat(
                    result.data.map((tc) => {
                        return {
                            value: tc,
                            label: tc,
                        };
                    })
                );
            }
        });
    }

    submitForm(form: NgForm): void {
        if (form.valid && this.setting) {
            this.setting.taxSettings = JSON.stringify(this.results);
            this.closemodal.emit(true);
            this.bsModalRef.hide();
        }
    }

    cancel(shareForm: NgForm): void {
        this.closemodal.emit(false);
        this.bsModalRef.hide();
    }

    // @ts-ignore TS7006
    changeDate(startDate): void {
        if (startDate && this.setting) this.setting.syncStartDate = startDate;
    }

    parseTaxRates() {
        this.TaxProfiles = this.flattenTaxProfile(this.SharedData.Common.TaxProfiles);
        this.expenseTaxCodes = this.mapToTaxCodes(TaxType.Expense, this.TaxProfiles);
        this.incomeTaxCodes = this.mapToTaxCodes(TaxType.Income, this.TaxProfiles);
    }

    flattenTaxProfile(taxProfile: any[]): FlattenedTaxProfile[] {
        return taxProfile
            .map((p) => {
                const rates = JSON.parse(p.TaxRates);
                // @ts-ignore TS7006
                return rates.map((r) => ({
                    code: r.code,
                    description: r.description,
                    type: r.type,
                    rate: r.rate,
                    defaults: r.defaults,
                    profileDescription: p.Description,
                    profileShortCode: p.ShortCode,
                    exempt: r.exempt,
                }));
            })
            .reduce((a, b) => a.concat(b), []);
    }

    mapToTaxCodes(type: number, profiles: FlattenedTaxProfile[]): TaxCode[] {
        const filteredProfiles = profiles.filter((x: { type: number }) => x.type === type);
        return (
            _(filteredProfiles)
                .groupBy((x) => x.code)
                // @ts-ignore TS2532
                .map((value, key) => ({ taxCode: key, taxDescription: value[0].description, taxProfiles: value }))
                .value()
        );
    }
}
